import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Send, Mail, AlertCircle, CheckCircle, Clock, User } from 'lucide-react';
import API_BASE_URL from '../config/api';
import { useClinic } from '../contexts/ClinicContext';

function OutboxPage() {
  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState({});
  const [sendingAll, setSendingAll] = useState(false);
  const navigate = useNavigate();
  const { activeClinicId } = useClinic();

  const fetchOutbox = useCallback(async () => {
    try {
      const clinicQuery = activeClinicId ? `?clinicId=${activeClinicId}` : '';
      const response = await axios.get(`${API_BASE_URL}/outbox${clinicQuery}`);
      setLetters(response.data);
    } catch (error) {
      console.error('Error fetching outbox:', error);
    } finally {
      setLoading(false);
    }
  }, [activeClinicId]);

  useEffect(() => {
    fetchOutbox();
  }, [fetchOutbox]);

  const sendLetter = async (letterId) => {
    setSending((prev) => ({ ...prev, [letterId]: true }));
    try {
      await axios.post(`${API_BASE_URL}/outbox/send/${letterId}`);
      await fetchOutbox();
    } catch (error) {
      console.error('Error sending letter:', error);
      alert('Failed to send letter');
    } finally {
      setSending((prev) => ({ ...prev, [letterId]: false }));
    }
  };

  const sendAll = async () => {
    if (!window.confirm('Send all letters in the outbox? This will email every pending letter to its recipients.')) {
      return;
    }
    setSendingAll(true);
    try {
      await axios.post(`${API_BASE_URL}/outbox/send-all`);
      await fetchOutbox();
    } catch (error) {
      console.error('Error sending all letters:', error);
      alert('Failed to send all letters');
    } finally {
      setSendingAll(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'sent':
        return { bg: '#dcfce7', text: '#166534', label: 'Sent' };
      case 'failed':
        return { bg: '#fee2e2', text: '#991b1b', label: 'Failed' };
      case 'pending':
      default:
        return { bg: '#fef3c7', text: '#92400e', label: 'Pending' };
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
        return <CheckCircle style={{ width: '14px', height: '14px' }} />;
      case 'failed':
        return <AlertCircle style={{ width: '14px', height: '14px' }} />;
      case 'pending':
      default:
        return <Clock style={{ width: '14px', height: '14px' }} />;
    }
  };

  const getLetterTypeBadge = (letterType) => {
    if (letterType === 'mri_referral') {
      return { bg: '#dbeafe', text: '#1e40af', label: 'Radiology Referral' };
    }
    return { bg: '#f3e8ff', text: '#7c3aed', label: 'Consultation' };
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('en-NZ');
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: '4px solid #e5e7eb',
          borderTop: '4px solid #2563eb',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '32px 24px' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '32px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h1 style={{ fontSize: '30px', fontWeight: 'bold', color: '#111827', margin: 0 }}>
              Outbox
            </h1>
            {letters.length > 0 && (
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '28px',
                height: '28px',
                padding: '0 8px',
                fontSize: '14px',
                fontWeight: '600',
                borderRadius: '14px',
                backgroundColor: '#2563eb',
                color: 'white'
              }}>
                {letters.length}
              </span>
            )}
          </div>
          {letters.length > 0 && (
            <button
              onClick={sendAll}
              disabled={sendingAll}
              style={{
                padding: '10px 20px',
                backgroundColor: sendingAll ? '#9ca3af' : '#2563eb',
                color: 'white',
                borderRadius: '8px',
                border: 'none',
                cursor: sendingAll ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                fontSize: '14px',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => !sendingAll && (e.currentTarget.style.backgroundColor = '#1d4ed8')}
              onMouseLeave={(e) => !sendingAll && (e.currentTarget.style.backgroundColor = '#2563eb')}
            >
              <Send style={{ width: '16px', height: '16px', marginRight: '8px' }} />
              {sendingAll ? 'Sending All...' : 'Send All'}
            </button>
          )}
        </div>

        {/* Empty State */}
        {letters.length === 0 && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            padding: '64px 24px',
            textAlign: 'center'
          }}>
            <Mail style={{ width: '64px', height: '64px', color: '#d1d5db', margin: '0 auto 16px' }} />
            <p style={{ color: '#6b7280', fontSize: '16px', margin: 0 }}>
              No letters ready to send
            </p>
          </div>
        )}

        {/* Letter Cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {letters.map((letter) => {
            const typeBadge = getLetterTypeBadge(letter.letterType);
            const isLetterSending = sending[letter.id];

            return (
              <div
                key={letter.id}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  overflow: 'hidden'
                }}
              >
                {/* Card Header */}
                <div style={{
                  padding: '16px 24px',
                  borderBottom: '1px solid #e5e7eb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: '12px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => navigate(`/patient-file/${letter.patientId}?tab=letters`)}
                      style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#2563eb',
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                        textDecoration: 'none'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                      onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                    >
                      {letter.patient?.name || 'Unknown Patient'}
                    </button>
                    {letter.patient?.nhi && (
                      <span style={{
                        fontSize: '13px',
                        color: '#6b7280',
                        fontWeight: '500'
                      }}>
                        NHI: {letter.patient.nhi}
                      </span>
                    )}
                    <span style={{
                      display: 'inline-block',
                      padding: '2px 10px',
                      fontSize: '11px',
                      fontWeight: '500',
                      borderRadius: '4px',
                      backgroundColor: typeBadge.bg,
                      color: typeBadge.text
                    }}>
                      {typeBadge.label}
                    </span>
                    <span style={{ fontSize: '13px', color: '#9ca3af' }}>
                      {formatDate(letter.generatedAt)}
                    </span>
                  </div>
                  <button
                    onClick={() => sendLetter(letter.id)}
                    disabled={isLetterSending}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: isLetterSending ? '#9ca3af' : '#2563eb',
                      color: 'white',
                      borderRadius: '8px',
                      border: 'none',
                      cursor: isLetterSending ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '14px',
                      fontWeight: '500',
                      flexShrink: 0
                    }}
                    onMouseEnter={(e) => !isLetterSending && (e.currentTarget.style.backgroundColor = '#1d4ed8')}
                    onMouseLeave={(e) => !isLetterSending && (e.currentTarget.style.backgroundColor = '#2563eb')}
                  >
                    <Send style={{ width: '14px', height: '14px', marginRight: '6px' }} />
                    {isLetterSending ? 'Sending...' : 'Send'}
                  </button>
                </div>

                {/* Recipients */}
                {letter.recipients && letter.recipients.length > 0 && (
                  <div style={{ padding: '12px 24px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {letter.recipients.map((recipient, index) => {
                        const statusBadge = getStatusBadge(recipient.status);
                        return (
                          <div
                            key={index}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '8px 12px',
                              backgroundColor: '#f9fafb',
                              borderRadius: '8px',
                              flexWrap: 'wrap',
                              gap: '8px'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                              <User style={{ width: '16px', height: '16px', color: '#9ca3af', flexShrink: 0 }} />
                              <span style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                                {recipient.name}
                              </span>
                              {recipient.type && (
                                <span style={{
                                  display: 'inline-block',
                                  padding: '1px 8px',
                                  fontSize: '10px',
                                  fontWeight: '500',
                                  borderRadius: '4px',
                                  backgroundColor: '#f3f4f6',
                                  color: '#4b5563',
                                  textTransform: 'capitalize'
                                }}>
                                  {recipient.type}
                                </span>
                              )}
                              <span style={{ fontSize: '13px', color: '#6b7280' }}>
                                {recipient.email}
                              </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '2px 10px',
                                fontSize: '11px',
                                fontWeight: '500',
                                borderRadius: '4px',
                                backgroundColor: statusBadge.bg,
                                color: statusBadge.text
                              }}>
                                {getStatusIcon(recipient.status)}
                                {statusBadge.label}
                              </span>
                              {recipient.sentAt && (
                                <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                                  {formatDate(recipient.sentAt)}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default OutboxPage;
